import { StrictMode, useEffect, useState } from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { MarketingApp } from './MarketingApp';
import type { MarketingPage } from './routes';
import { defaultContent, fetchPublishedContent, mergeContent, type SiteContent } from './siteContent';
import { isPreviewRequest, PREVIEW_STORAGE_KEY, readPreview } from './preview';

/** Content baked into the page at build time (keeps hydration identical to the server render). */
function readEmbeddedContent(): SiteContent | null {
  const node = document.getElementById('mx-content');
  if (!node?.textContent) return null;
  try { return mergeContent(defaultContent, JSON.parse(node.textContent)); } catch { return null; }
}

function LiveSite({ page, initial }: { page: MarketingPage; initial: SiteContent }) {
  const [content, setContent] = useState(initial);
  const [preview, setPreview] = useState(false);
  useEffect(() => { setPreview(isPreviewRequest()); }, []);
  useEffect(() => {
    if (!preview) return;
    // Preview opened from the admin editor: show the editor's working copy and follow its changes live.
    const apply = () => { const draft = readPreview(); if (draft) setContent(mergeContent(defaultContent, draft)); };
    apply();
    const onStorage = (event: StorageEvent) => { if (event.key === PREVIEW_STORAGE_KEY) apply(); };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [preview]);
  useEffect(() => {
    if (preview) return;
    // Pick up anything published from the admin dashboard after this page was built.
    let cancelled = false;
    void fetchPublishedContent().then(latest => {
      if (cancelled || !latest || JSON.stringify(latest) === JSON.stringify(initial)) return;
      setContent(latest);
    });
    return () => { cancelled = true; };
  }, [initial, preview]);
  useEffect(() => {
    const meta = content.seo[page];
    if (meta?.title) document.title = meta.title;
    if (meta?.description) document.querySelector('meta[name="description"]')?.setAttribute('content', meta.description);
  }, [content, page]);
  return <>{preview && <div className="mx-preview-bar" role="status">وضع المعاينة — هذا المحتوى من محرر الموقع ولم يُنشر للزوار بعد <a href={window.location.pathname}>عرض المنشور</a></div>}<MarketingApp page={page} content={content} /></>;
}

export function mountMarketing(root: HTMLElement, page: MarketingPage) {
  document.documentElement.classList.add('mx-js');
  const embedded = readEmbeddedContent();
  const tree = <StrictMode><LiveSite page={page} initial={embedded ?? defaultContent} /></StrictMode>;
  // Pages are pre-rendered at build time (scripts/prerender-marketing.mjs); hydrate when markup exists.
  if (root.firstElementChild && embedded) hydrateRoot(root, tree);
  else { root.textContent = ''; createRoot(root).render(tree); }
  if (window.location.hash) requestAnimationFrame(() => document.getElementById(decodeURIComponent(window.location.hash.slice(1)))?.scrollIntoView());
}
